import React, { ForwardedRef } from 'react';
import { To } from 'react-router-dom';
import { Icon } from 'shared/components/ui/icon';
import { QueryParamOptions } from 'shared/lib/react-router-dom';
import { UtmParameters } from 'shared/types/utm-parameters';

import * as Styled from './side-navigation-item.style';

export type SideNavigationItemProps = {
  label: string;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  start?: React.ReactNode;
  link?: To;
  activeSearchParamsToIgnore?: (QueryParamOptions | UtmParameters)[];
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  active?: boolean;
  children?: React.ReactNode;
  action?: React.ReactNode;
  end?: React.ReactNode;
  isMoving?: boolean;
  prominent?: boolean;
};

export const SideNavigationItem = React.forwardRef<
  HTMLAnchorElement | HTMLDivElement,
  SideNavigationItemProps
>(
  (
    {
      label,
      onClick,
      onContextMenu,
      start,
      link,
      activeSearchParamsToIgnore,
      icon,
      active,
      children,
      action,
      end,
      isMoving,
      prominent,
    },
    ref,
  ) => {
    const content = (
      <>
        {!!start && (
          <Styled.SubItemIcon $prominent={prominent}>
            {start}
          </Styled.SubItemIcon>
        )}

        {!!icon && (
          <Styled.SubItemIcon $prominent={prominent}>
            <Icon icon={icon} />
          </Styled.SubItemIcon>
        )}

        <Styled.LinkTitle>{label}</Styled.LinkTitle>

        <Styled.EndContainer>
          <Styled.Content>{children}</Styled.Content>
          {!!action && <Styled.Action>{action}</Styled.Action>}
          {!!end && <Styled.End>{end}</Styled.End>}
        </Styled.EndContainer>
      </>
    );

    if (link) {
      return (
        <Styled.Link
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          to={link}
          $active={!!active}
          $isMoving={isMoving}
          $prominent={prominent}
          onClick={onClick}
          onContextMenu={onContextMenu}
          activeSearchParamsToIgnore={activeSearchParamsToIgnore}
        >
          {content}
        </Styled.Link>
      );
    }

    if (onClick) {
      return (
        <Styled.Button
          ref={ref as ForwardedRef<HTMLButtonElement>}
          type="button"
          $active={!!active}
          $isMoving={isMoving}
          $prominent={prominent}
          onClick={onClick}
          onContextMenu={onContextMenu}
        >
          {content}
        </Styled.Button>
      );
    }

    return (
      <Styled.DisabledLink
        ref={ref as ForwardedRef<HTMLSpanElement>}
        $isMoving={isMoving}
        $prominent={prominent}
        onContextMenu={onContextMenu}
      >
        {content}
      </Styled.DisabledLink>
    );
  },
);
